import { useState } from 'react'
import { calculateAssignmentResult, createPositionFromAssignment, calculateSoldResult } from '@/lib/positions'
import { supabase } from '@/lib/supabase'
import type { Position } from '@/types'

interface CloseTradeFormProps {
  trade: any
  positions?: Position[]
  accountSlug?: string
  onSubmit: (closeData: any) => void
  onCancel: () => void
}

export function CloseTradeForm({ trade, positions = [], accountSlug = '', onSubmit, onCancel }: CloseTradeFormProps) {
  const [form, setForm] = useState({
    closeAction: 'Buy Back',
    closeDate: new Date().toISOString().split('T')[0],
    closePremium: '',
    closeCommission: '0.65',
    closeFees: '0.02',
    soldPrice: '',
    sharesSold: '',
  })
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Find position for this ticker (for Sold action)
  const tickerPositions = positions.filter(p => p.ticker === trade.ticker && p.status === 'open')
  const linkedPosition = tickerPositions.length > 0 ? tickerPositions[0] : null

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.closeDate) {
      setError('Close date is required')
      return
    }

    const closePremium = parseFloat(form.closePremium) || 0
    const closeCommission = -Math.abs(parseFloat(form.closeCommission) || 0)
    const closeFees = -Math.abs(parseFloat(form.closeFees) || 0)

    let closeTotal = 0
    let netPosition = 0
    let extraFields: Record<string, any> = {}

    if (form.closeAction === 'Buy Back' || form.closeAction === 'Rolled') {
      closeTotal = -((closePremium * 100 * trade.qty) + Math.abs(closeCommission) + Math.abs(closeFees))
      netPosition = trade.total + closeTotal
    } else if (form.closeAction === 'Expired Worthless') {
      closeTotal = 0
      netPosition = trade.total
    } else if (form.closeAction === 'Assigned') {
      // Fixed: Assignment is NOT a loss. Premium is the profit.
      const result = calculateAssignmentResult(trade)
      closeTotal = result.closeTotal  // 0
      netPosition = result.netPosition  // trade.total (premium is profit)

      // Create position via API
      setSubmitting(true)
      try {
        const positionData = createPositionFromAssignment(trade, form.closeDate)
        // Only pass linked_trade_id if it's a valid UUID (not a legacy "trade_<timestamp>" id)
        const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
        const linkedTradeId = UUID_REGEX.test(positionData.sourceTradeId) ? positionData.sourceTradeId : null
        const { error: insertError } = await supabase.from('positions').insert({
          account_slug: accountSlug || null,
          ticker: positionData.ticker,
          quantity: positionData.quantity,
          cost_basis: positionData.costBasis,
          assigned_date: positionData.acquisitionDate,
          linked_trade_id: linkedTradeId,
          status: 'open',
          metadata: {},
          user_id: '00000000-0000-0000-0000-000000000000',
        })
        if (insertError) {
          setError('Failed to create position. Please try again.')
          console.error('Position insert error:', insertError)
          setSubmitting(false)
          return
        }
      } catch {
        setError('Failed to create position. Please try again.')
        setSubmitting(false)
        return
      }
      setSubmitting(false)
    } else if (form.closeAction === 'Sold (Shares)') {
      // Validate sold price
      const soldPrice = parseFloat(form.soldPrice)
      if (!form.soldPrice || isNaN(soldPrice) || soldPrice <= 0) {
        setError('Sold price per share is required and must be greater than 0')
        return
      }

      const sharesSold = parseInt(form.sharesSold) || (linkedPosition?.quantity || 100)
      if (linkedPosition && sharesSold > linkedPosition.quantity) {
        setError(`Shares sold cannot exceed position quantity (${linkedPosition.quantity})`)
        return
      }

      const costBasis = linkedPosition?.costBasis || trade.strikePrice
      const soldResult = calculateSoldResult(trade, soldPrice, sharesSold, costBasis)

      closeTotal = 0
      netPosition = soldResult.netPosition
      extraFields = {
        soldPrice,
        sharesSold,
        shareAppreciation: soldResult.shareAppreciation,
      }

      // Update position via API (reduce quantity or close)
      if (linkedPosition) {
        setSubmitting(true)
        try {
          const newQty = linkedPosition.quantity - sharesSold
          const updateData: Record<string, any> = {
            quantity: newQty,
            status: newQty <= 0 ? 'closed' : 'open',
            sold_price: soldPrice,
          }
          if (newQty <= 0) {
            updateData.sold_date = form.closeDate
          }
          const { error: updateError } = await supabase
            .from('positions')
            .update(updateData)
            .eq('id', linkedPosition.id)
          if (updateError) {
            setError('Failed to update position. Please try again.')
            setSubmitting(false)
            return
          }
        } catch {
          setError('Failed to update position. Please try again.')
          setSubmitting(false)
          return
        }
        setSubmitting(false)
      }
    }

    const buyDate = new Date(trade.buyDate)
    const closeDate = new Date(form.closeDate)
    const daysKept = Math.ceil((closeDate.getTime() - buyDate.getTime()) / (1000 * 60 * 60 * 24))
    const returnPct = trade.strikePrice > 0 ? (netPosition / (trade.strikePrice * 100 * trade.qty)) * 100 : 0

    onSubmit({
      ...trade,
      status: 'closed',
      closeAction: form.closeAction,
      closeDate: form.closeDate,
      closePremium,
      closeCommission,
      closeFees,
      closeTotal: Math.round(closeTotal * 100) / 100,
      netPosition: Math.round(netPosition * 100) / 100,
      daysKept,
      daysLeft: 0,
      returnPct: Math.round(returnPct * 100) / 100,
      ...extraFields,
    })
  }

  // Assignment summary display
  const assignmentSummary = form.closeAction === 'Assigned' ? calculateAssignmentResult(trade) : null

  // Sold summary display
  const soldPrice = parseFloat(form.soldPrice) || 0
  const sharesSold = parseInt(form.sharesSold) || (linkedPosition?.quantity || trade.qty * 100)
  const costBasis = linkedPosition?.costBasis || trade.strikePrice
  const soldPreview = form.closeAction === 'Sold (Shares)' && soldPrice > 0
    ? calculateSoldResult(trade, soldPrice, sharesSold, costBasis)
    : null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-6" onClick={onCancel}>
      <div className="bg-white rounded-2xl p-5 sm:p-8 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        <h2 className="text-xl font-bold text-[var(--color-text-primary)]">Close Trade</h2>
        <p className="text-sm text-[var(--color-text-muted)] mt-1">
          {trade.ticker} {trade.action} ${trade.strikePrice} — Premium: ${trade.premium} × {trade.qty}
        </p>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Close Action</label>
              <select
                value={form.closeAction}
                onChange={(e) => setForm(f => ({ ...f, closeAction: e.target.value }))}
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
                style={{ height: '40px' }}
                tabIndex={0}
              >
                <option value="Buy Back">Buy Back</option>
                <option value="Expired Worthless">Expired Worthless</option>
                <option value="Assigned">Assigned</option>
                <option value="Rolled">Rolled</option>
                <option value="Sold (Shares)">Sold (Shares)</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Close Date</label>
              <input
                type="date"
                value={form.closeDate}
                onChange={(e) => setForm(f => ({ ...f, closeDate: e.target.value }))}
                className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
                style={{ height: '40px' }}
                tabIndex={0}
              />
            </div>
          </div>

          {(form.closeAction === 'Buy Back' || form.closeAction === 'Rolled') && (
            <div className="space-y-5">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Buy Back Premium</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.closePremium}
                  onChange={(e) => setForm(f => ({ ...f, closePremium: e.target.value }))}
                  placeholder="0.50"
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
                  style={{ height: '40px' }}
                  tabIndex={0}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Commission</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.closeCommission}
                    onChange={(e) => setForm(f => ({ ...f, closeCommission: e.target.value }))}
                    className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
                    style={{ height: '40px' }}
                    tabIndex={0}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Fees</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.closeFees}
                    onChange={(e) => setForm(f => ({ ...f, closeFees: e.target.value }))}
                    className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
                    style={{ height: '40px' }}
                    tabIndex={0}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Assignment summary */}
          {form.closeAction === 'Assigned' && assignmentSummary && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
              <h4 className="text-sm font-semibold text-blue-800">Assignment Summary</h4>
              <div className="text-sm text-blue-700 space-y-1">
                <div className="flex justify-between">
                  <span>Shares Acquired:</span>
                  <span className="font-medium">{assignmentSummary.sharesAcquired} shares of {trade.ticker}</span>
                </div>
                <div className="flex justify-between">
                  <span>Cost Basis:</span>
                  <span className="font-mono">${assignmentSummary.costBasis.toFixed(2)}/share</span>
                </div>
                <div className="flex justify-between">
                  <span>Premium Profit:</span>
                  <span className="font-mono text-green-700">${trade.total.toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          {/* Sold (Shares) fields */}
          {form.closeAction === 'Sold (Shares)' && (
            <div className="space-y-5">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Sold Price Per Share</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.soldPrice}
                  onChange={(e) => setForm(f => ({ ...f, soldPrice: e.target.value }))}
                  placeholder="200.00"
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
                  style={{ height: '40px' }}
                  tabIndex={0}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-[var(--color-text-muted)] mb-2">Shares Sold</label>
                <input
                  type="number"
                  step="1"
                  value={form.sharesSold || (linkedPosition?.quantity || trade.qty * 100)}
                  onChange={(e) => setForm(f => ({ ...f, sharesSold: e.target.value }))}
                  className="px-3 py-2 border border-[var(--color-border)] rounded-lg bg-white"
                  style={{ height: '40px' }}
                  tabIndex={0}
                />
                {linkedPosition && (
                  <span className="text-xs text-[var(--color-text-muted)]">
                    Position: {linkedPosition.quantity} shares @ ${linkedPosition.costBasis.toFixed(2)}
                  </span>
                )}
              </div>

              {/* Sold preview */}
              {soldPreview && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-1">
                  <h4 className="text-sm font-semibold text-green-800">Sale Summary</h4>
                  <div className="text-sm text-green-700 space-y-1">
                    <div className="flex justify-between">
                      <span>Share Appreciation:</span>
                      <span className={`font-mono ${soldPreview.shareAppreciation >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        ${soldPreview.shareAppreciation.toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>CC Premium + Appreciation:</span>
                      <span className={`font-mono font-medium ${soldPreview.netPosition >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                        ${soldPreview.netPosition.toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {error && <p className="text-sm text-[var(--color-red)]">{error}</p>}

          <div className="flex justify-end gap-3 pt-6 mt-2 border-t border-[var(--color-border)]">
            <button type="button" onClick={onCancel} className="px-6 py-2.5 rounded-lg border border-[var(--color-border)] text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-tertiary)]" style={{ height: '44px' }}>Cancel</button>
            <button
              type="submit"
              disabled={submitting}
              className="px-6 py-2.5 rounded-lg bg-[var(--color-green)] text-white font-medium hover:bg-[var(--color-green)]/80 disabled:opacity-50"
              style={{ height: '44px' }}
            >
              {submitting ? 'Processing...' : 'Close Trade'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
